import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input, Select, message } from 'antd';
import StatusChip from './components/StatusChip';
import PriorityBadge from './components/PriorityBadge';
import AgeBadge from './components/AgeBadge';
import { fetchSamples, exportSamplesCSV, updateSampleStatus, updateSamplePriority } from './api';
import { useFilters } from './FilterContext';
import { useMasters } from './MastersContext';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const TYPE_STYLES = {
  Routine:   { bg: '#e0f2fe', color: '#0369a1' },
  Stability: { bg: '#f5f3ff', color: '#7c3aed' },
  Support:   { bg: '#fffbeb', color: '#d97706' },
};

const MARKET_STYLES = {
  US: { bg: '#dbeafe', color: '#1d4ed8' },
  EU: { bg: '#d1fae5', color: '#065f46' },
  EP: { bg: '#ede9fe', color: '#5b21b6' },
};

const SampleLog = () => {
  const { filters, buildQueryParams } = useFilters();
  const { ageingBands, masters } = useMasters();

  const [data, setData] = useState({ samples: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('');
  const [search, setSearch] = useState('');
  const [colFilters, setColFilters] = useState({}); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const debounceRef = useRef(null);

  /* ---- Data fetching ---- */
  const loadData = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const globalParams = buildQueryParams();
      const result = await fetchSamples({
        ...globalParams,
        search,
        sortBy: sortField,
        sortDir,
        page,
        pageSize: 25,
      });
      setData(result);
      setCurrentPage(page);
    } catch (err) {
      message.error('Failed to load samples');
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, search, sortField, sortDir]);

  useEffect(() => {
    loadData(1);
  }, [filters, search, sortField, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- Search debounce ---- */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(val), 300);
  };

  /* ---- Export ---- */
  const handleExport = async () => {
    try {
      const globalParams = buildQueryParams();
      await exportSamplesCSV({ ...globalParams, search, sortBy: sortField, sortDir });
      message.success('CSV exported');
    } catch {
      message.error('Export failed');
    }
  };

  /* ---- Priority toggle ---- */
  const handlePriorityToggle = async (record) => {
    const next = record.priority === 'Urgent' ? 'Normal' : 'Urgent';
    try {
      await updateSamplePriority(record.id, next);
      loadData(currentPage);
    } catch {
      message.error('Failed to update priority');
    }
  };

  /* ---- Action handlers ---- */
  const handleAction = async (id, status) => {
    try {
      await updateSampleStatus(id, status);
      loadData(currentPage);
    } catch {
      message.error('Failed to update status');
    }
  };

  /* ---- Sorting ---- */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? '' : 'asc');
      if (sortDir === 'desc') setSortField('');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return <span style={{ opacity: 0.4, marginLeft: 2 }}>↕</span>;
    return <span style={{ opacity: 1, marginLeft: 2 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  /* ---- Render helpers ---- */
  const renderTypeChip = (type) => {
    const s = TYPE_STYLES[type] || { bg: '#f3f4f6', color: '#6b7280' };
    return (
      <span style={{
        display: 'inline-block', padding: '1px 5px', borderRadius: 2,
        fontSize: 8, fontWeight: 700, backgroundColor: s.bg, color: s.color,
      }}>
        {type}
      </span>
    );
  };

  const renderMarketBadge = (market) => {
    const s = MARKET_STYLES[market] || { bg: '#f3f4f6', color: '#6b7280' };
    return (
      <span style={{
        display: 'inline-block', padding: '1px 5px', borderRadius: 2,
        fontSize: 8, fontWeight: 700, backgroundColor: s.bg, color: s.color,
      }}>
        {market}
      </span>
    );
  };

  const renderActions = (record) => {
    if (record.status === 'Completed' || record.status === 'Cancelled') return null;
    return (
      <div style={{ display: 'flex', gap: 2 }}>
        {record.status === 'Pending' && (
          <button
            onClick={() => handleAction(record.id, 'Running')}
            style={{ fontSize: 7, fontWeight: 700, padding: '2px 5px', borderRadius: 2, border: 'none', cursor: 'pointer', background: '#fffbeb', color: '#d97706', marginRight: 2 }}
          >RUN</button>
        )}
        {record.status === 'Running' && (
          <button
            onClick={() => handleAction(record.id, 'Completed')}
            style={{ fontSize: 7, fontWeight: 700, padding: '2px 5px', borderRadius: 2, border: 'none', cursor: 'pointer', background: '#f0fdf4', color: '#059669', marginRight: 2 }}
          >DONE</button>
        )}
        <button
          onClick={() => handleAction(record.id, 'Cancelled')}
          style={{ fontSize: 7, fontWeight: 700, padding: '2px 5px', borderRadius: 2, border: 'none', cursor: 'pointer', background: '#fef2f2', color: '#dc2626' }}
        >CANC</button>
      </div>
    );
  };

  /* ---- Column definitions ---- */
  const COLS = [
    { key: 'star', label: '★', w: 35 },
    { key: 'id', label: 'No', w: 45, sortable: true, field: 'id' },
    { key: 'receiptDate', label: 'Date', w: 80, sortable: true, field: 'receipt_date' },
    { key: 'arNumber', label: 'AR No.', w: 90, sortable: true, field: 'ar_number' },
    { key: 'productName', label: 'Product', w: 140, sortable: true, field: 'product_name' },
    { key: 'analysisType', label: 'Type', w: 75 },
    { key: 'market', label: 'Market', w: 55 },
    { key: 'stage', label: 'Stage', w: 65 },
    { key: 'strength', label: 'Strength', w: 60 },
    { key: 'testName', label: 'Test', w: 85 },
    { key: 'duration', label: 'Duration', w: 60 },
    { key: 'dateOfAnalysis', label: 'DOA', w: 80 },
    { key: 'ageDays', label: 'Age', w: 48 },
    { key: 'priority', label: 'Priority', w: 65 },
    { key: 'status', label: 'Status', w: 75 },
    { key: 'actions', label: 'Actions', w: 70 },
  ];

  const renderCell = (col, record) => {
    switch (col.key) {
      case 'star':
        return <PriorityBadge priority={record.priority} status={record.status} onClick={() => handlePriorityToggle(record)} />;
      case 'id':
        return record.id;
      case 'receiptDate':
        return <span style={{ ...MONO, fontSize: 8, color: '#9ca3af' }}>{record.receiptDate}</span>;
      case 'arNumber':
        return <span style={{ ...MONO, fontSize: 9, color: '#1a3353', fontWeight: 600 }}>{record.arNumber}</span>;
      case 'productName':
        return <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 140 }}>{record.productName}</span>;
      case 'analysisType':
        return renderTypeChip(record.analysisType);
      case 'market':
        return renderMarketBadge(record.market);
      case 'stage':
        return <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 65 }}>{record.stage}</span>;
      case 'strength':
        return record.strength;
      case 'testName':
        return <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 85 }}>{record.testName}</span>;
      case 'duration':
        return record.duration;
      case 'dateOfAnalysis':
        return <span style={{ ...MONO, fontSize: 8, color: '#9ca3af' }}>{record.dateOfAnalysis}</span>;
      case 'ageDays':
        return <AgeBadge ageDays={record.ageDays} ageingBands={ageingBands} />;
      case 'priority':
        return (
          <span style={{
            display: 'inline-block', padding: '1px 5px', borderRadius: 2,
            fontSize: 8, fontWeight: 700,
            backgroundColor: record.priority === 'Urgent' ? '#fff7ed' : '#f3f4f6',
            color: record.priority === 'Urgent' ? '#ea580c' : '#6b7280',
          }}>
            {record.priority}
          </span>
        );
      case 'status':
        return <StatusChip status={record.status} />;
      case 'actions':
        return renderActions(record);
      default:
        return null;
    }
  };

  /* ---- Pagination ---- */
  const totalPages = data.totalPages || Math.ceil(data.total / 25);

  // Sliding window: always show first, last, current ±2, with ellipsis
  const pageNums = (() => {
    if (totalPages <= 9) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages]);
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
      result.push(sorted[i]);
    }
    return result;
  })();

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Top bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #d1d5db',
        padding: '4px 14px', display: 'flex', alignItems: 'center', gap: 5,
        flexShrink: 0, flexWrap: 'wrap',
      }}>
        <input
          type="text"
          placeholder="🔍 Search AR#, product, batch, test..."
          onChange={handleSearchChange}
          style={{
            flex: 1, minWidth: 150, maxWidth: 220,
            padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 4,
            fontFamily: "'DM Sans', sans-serif", fontSize: 10, outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = '#2563eb'}
          onBlur={e => e.target.style.borderColor = '#d1d5db'}
        />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: '#4b5563', whiteSpace: 'nowrap', fontWeight: 600 }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#1a3353', fontSize: 12 }}>{data.total}</span>
          {' '}records &nbsp;·&nbsp; page {currentPage} of {totalPages}
        </span>
        <button
          onClick={handleExport}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '3px 9px', background: '#f0fdf4', border: '1px solid #6ee7b7',
            borderRadius: 4, fontSize: 9, fontWeight: 700, color: '#059669',
            cursor: 'pointer',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#059669'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.color = '#059669'; }}
        >
          ⬇ Export CSV
        </button>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, whiteSpace: 'nowrap' }}>
          <thead>
            <tr>
              {COLS.map(col => (
                <th
                  key={col.key}
                  onClick={col.sortable ? () => handleSort(col.field) : undefined}
                  style={{
                    position: 'sticky', top: 0, zIndex: 10,
                    background: '#1a3353', color: '#fff',
                    padding: '5px 7px', fontSize: 8, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.3,
                    textAlign: 'left', cursor: col.sortable ? 'pointer' : 'default',
                    userSelect: 'none', whiteSpace: 'nowrap',
                    width: col.w, minWidth: col.w,
                  }}
                >
                  {col.label}
                  {col.sortable && sortIcon(col.field)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.samples.map((record, idx) => (
              <tr
                key={record.id}
                style={{ background: idx % 2 === 1 ? '#fafbfc' : '#fff' }}
                onMouseEnter={e => e.currentTarget.style.background = '#eef3ff'}
                onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 1 ? '#fafbfc' : '#fff'}
              >
                {COLS.map(col => (
                  <td key={col.key} style={{
                    padding: '3px 7px', borderBottom: '1px solid #e5e7eb',
                    width: col.w, minWidth: col.w, maxWidth: col.w,
                    overflow: 'hidden',
                  }}>
                    {renderCell(col, record)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>Loading...</div>}
      </div>

      {/* Paginator */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
        padding: 5, background: '#fff', borderTop: '1px solid #d1d5db', flexShrink: 0,
      }}>
        <button
          onClick={() => currentPage > 1 && loadData(currentPage - 1)}
          disabled={currentPage <= 1}
          style={{
            padding: '2px 7px', border: '1px solid #d1d5db', borderRadius: 3,
            fontSize: 9, cursor: currentPage > 1 ? 'pointer' : 'default',
            background: '#f9fafb', color: '#4b5563', opacity: currentPage <= 1 ? 0.4 : 1,
          }}
        >
          ‹
        </button>
        {pageNums.map((n, i) =>
          n === '...'
            ? <span key={`e${i}`} style={{ fontSize: 9, color: '#9ca3af', padding: '0 2px' }}>…</span>
            : (
              <button
                key={n}
                onClick={() => loadData(n)}
                style={{
                  padding: '2px 7px', border: '1px solid ' + (n === currentPage ? '#2563eb' : '#d1d5db'),
                  borderRadius: 3, fontSize: 9, cursor: 'pointer',
                  background: n === currentPage ? '#2563eb' : '#f9fafb',
                  color: n === currentPage ? '#fff' : '#4b5563',
                  fontWeight: n === currentPage ? 700 : 400,
                }}
              >
                {n}
              </button>
            )
        )}
        <button
          onClick={() => currentPage < totalPages && loadData(currentPage + 1)}
          disabled={currentPage >= totalPages}
          style={{
            padding: '2px 7px', border: '1px solid #d1d5db', borderRadius: 3,
            fontSize: 9, cursor: currentPage < totalPages ? 'pointer' : 'default',
            background: '#f9fafb', color: '#4b5563', opacity: currentPage >= totalPages ? 0.4 : 1,
          }}
        >
          ›
        </button>
      </div>
    </div>
  );
};

export default SampleLog;
